import { useGetAllCategories } from "@/api/queries";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import placeholder from "@/assets/img/placeholder.jpg";

interface FeaturedCategoryItemProps {
  title: string;
  image: string | null | undefined;
  link: string;
  count?: number;
}

const FeaturedCategoryItem: React.FC<FeaturedCategoryItemProps> = ({
  title,
  image,
  link,
  count,
}) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(link);
  };

  return (
    <div
      onClick={handleClick}
      className='block relative overflow-hidden rounded-lg shadow-md group h-32 cursor-pointer'
    >
      <img
        src={image || placeholder}
        alt={title}
        className='w-full h-full object-cover transition-transform duration-300 group-hover:scale-110'
        onError={(e) => {
          // Fallback to placeholder if image fails to load
          const target = e.target as HTMLImageElement;
          target.src = placeholder;
        }}
      />
      <div className='absolute inset-0 bg-black/60 flex flex-col items-center justify-center'>
        <h3 className='text-white text-lg font-semibold text-center px-2'>{title}</h3>
        {count !== undefined && <p className='text-white/80 text-sm mt-1'>{count} products</p>}
      </div>
    </div>
  );
};

export const FeaturedCategory = () => {
  const { data, isLoading, error } = useGetAllCategories();

  const featuredCategories = useMemo(() => {
    if (!data?.data) return [];

    // Shuffle categories randomly
    const shuffled = [...data.data].sort(() => 0.5 - Math.random());

    return shuffled.slice(0, 12);
  }, [data]);

  // Loading state
  if (isLoading) {
    return (
      <div className='bg-white py-16'>
        <div className='container mx-auto px-4'>
          <h2 className='text-2xl font-bold mb-8'>Shop by Category</h2>
          <div className='flex justify-center items-center py-12'>
            <div className='flex items-center space-x-2'>
              <Loader2 className='h-6 w-6 animate-spin' />
              <span>Loading categories...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='bg-white py-16'>
        <div className='container mx-auto px-4'>
          <h2 className='text-2xl font-bold mb-8'>Shop by Category</h2>
          <div className='text-center py-12'>
            <p className='text-red-600 mb-4'>Failed to load categories</p>
            <button
              onClick={() => window.location.reload()}
              className='px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90'
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No categories state
  if (featuredCategories.length === 0) {
    return (
      <div className='bg-white py-16'>
        <div className='container mx-auto px-4'>
          <h2 className='text-2xl font-bold mb-8'>Shop by Category</h2>
          <div className='text-center py-12'>
            <div className='text-6xl mb-4'>📂</div>
            <p className='text-gray-600'>No categories available at the moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white py-4'>
      <div className='container mx-auto px-4'>
        <h2 className='text-2xl font-bold mb-8'>Shop by Category</h2>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
          {featuredCategories.map((category) => (
            <FeaturedCategoryItem
              key={category.id}
              title={category.name}
              image={category.image}
              link={`/shop/category/${category.id}`}
              count={category.count}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
